import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/data-display/StatusBadge';
import { PaymentRecord } from '@/types/domain/payment-management';
import { mockPaymentService } from '@/services/mock/MockPaymentService';
import { formatInr, formatDate } from '@/lib/utils';
import {
  ArrowLeft,
  IndianRupee,
  CheckCircle2,
  AlertTriangle,
  FileText,
  MessageSquare,
  History,
  Database,
  RefreshCw,
  ExternalLink,
  ShieldCheck,
  FileCheck,
  CreditCard,
  Layers,
  Landmark,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { DocumentAttachment, RequestComment } from '@/types/domain';
import { mockDocumentCommentService } from '@/services/mock/MockDocumentCommentService';
import { DocumentManagementPanel } from '@/components/data-display/DocumentManagementPanel';
import { EnterpriseCommentThread } from '@/components/data-display/EnterpriseCommentThread';
import { PaymentAuditHistoryTab } from '@/features/payments/components/PaymentAuditHistoryTab';
import { PaymentSapSyncModal } from '@/features/payments/components/PaymentSapSyncModal';
import { PaymentReconcileModal } from '@/features/payments/components/PaymentReconcileModal';
import { getAvailablePaymentActions } from '@/features/payments/utils/paymentTransitions';
import { usePermission } from '@/hooks/usePermission';

export interface PaymentDetailPageProps {
  recordId?: string;
  isDrawer?: boolean;
  onClose?: () => void;
}

export const PaymentDetailPage: React.FC<PaymentDetailPageProps> = ({
  recordId,
  isDrawer,
}) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = recordId || paramId;
  const navigate = useNavigate();
  const { can } = usePermission();

  const [payment, setPayment] = useState<PaymentRecord | null>(null);
  const [documents, setDocuments] = useState<DocumentAttachment[]>([]);
  const [comments, setComments] = useState<RequestComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<
    'overview' | 'traceability' | 'readiness' | 'allocations' | 'failure' | 'reconciliation' | 'sap' | 'documents' | 'comments' | 'audit'
  >('overview');

  // Modals
  const [isSapModalOpen, setIsSapModalOpen] = useState(false);
  const [isReconcileModalOpen, setIsReconcileModalOpen] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const loadPayment = useCallback(async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const [data, docData, commentData] = await Promise.all([
        mockPaymentService.getPaymentById(id),
        mockDocumentCommentService.getDocuments('PAYMENT', id),
        mockDocumentCommentService.getComments('PAYMENT', id),
      ]);
      setPayment(data);
      setDocuments(docData);
      setComments(commentData);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadPayment();
  }, [loadPayment]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-3 font-heading">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
        <p className="text-xs text-foreground-muted">Loading Treasury Disbursement Record...</p>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4 font-heading">
        <AlertTriangle className="h-10 w-10 text-amber-500" />
        <h2 className="text-lg font-bold text-foreground">Payment Record Not Found</h2>
        <Button variant="outline" size="sm" onClick={() => navigate(ROUTES.PAYMENTS.LIST)}>
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Payments
        </Button>
      </div>
    );
  }

  const availableActions = getAvailablePaymentActions(payment, { can });

  const handleExecute = async () => {
    try {
      setIsActionLoading(true);
      await mockPaymentService.executePayment(payment.id);
      await loadPayment();
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setIsActionLoading(true);
      await mockPaymentService.approvePayment(payment.id, 'Approved via Treasury Command Center');
      await loadPayment();
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleRetry = async () => {
    try {
      setIsActionLoading(true);
      await mockPaymentService.retryPayment(payment.id);
      await loadPayment();
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleHoldToggle = async () => {
    try {
      setIsActionLoading(true);
      if (payment.status === 'ON_HOLD') {
        await mockPaymentService.releaseHold(payment.id);
      } else {
        await mockPaymentService.placeOnHold(payment.id, 'Treasury review hold');
      }
      await loadPayment();
    } finally {
      setIsActionLoading(false);
    }
  };

  return (
    <div className={`space-y-4 font-poppins ${isDrawer ? 'pb-2' : 'pb-12 max-w-7xl mx-auto'}`}>
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {!isDrawer && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ROUTES.PAYMENTS.LIST)}
              className="h-9 w-9 p-0 font-poppins cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-xl font-black text-foreground font-poppins">{payment.paymentReference}</h1>
              <StatusBadge status={payment.status} category="Payment" />
              {payment.isOverdue && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase bg-rose-500/10 text-rose-600 border border-rose-500/20 font-poppins">
                  {payment.daysOverdue} Days Overdue
                </span>
              )}
            </div>
            <p className="text-xs text-foreground-muted mt-0.5 font-poppins">
              Electronic Disbursement cleared via {payment.paymentMethod} • Company Code: {payment.companyCode}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          {availableActions.find((a) => a.action === 'EXECUTE_PAYMENT')?.isAvailable && (
            <Button size="sm" onClick={handleExecute} disabled={isActionLoading}>
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Execute Disbursement
            </Button>
          )}

          {availableActions.find((a) => a.action === 'APPROVE_PAYMENT')?.isAvailable && (
            <Button size="sm" onClick={handleApprove} disabled={isActionLoading}>
              <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve Payment
            </Button>
          )}

          {availableActions.find((a) => a.action === 'RETRY_PAYMENT')?.isAvailable && (
            <Button
              size="sm"
              variant="outline"
              className="text-rose-600 border-rose-500/30 hover:bg-rose-500/10"
              onClick={handleRetry}
              disabled={isActionLoading}
            >
              <RefreshCw className="h-4 w-4 mr-1.5" /> Retry Payment ({payment.retryCount}/{payment.maxRetries})
            </Button>
          )}

          {availableActions.find((a) => a.action === 'RECONCILE_PAYMENT')?.isAvailable && (
            <Button
              size="sm"
              variant="outline"
              className="text-amber-600 border-amber-500/30 hover:bg-amber-500/10"
              onClick={() => setIsReconcileModalOpen(true)}
            >
              <FileCheck className="h-4 w-4 mr-1.5" /> Reconcile Variance
            </Button>
          )}

          {availableActions.find((a) => a.action === 'PLACE_ON_HOLD')?.isAvailable && (
            <Button size="sm" variant="outline" onClick={handleHoldToggle} disabled={isActionLoading}>
              Hold Payment
            </Button>
          )}

          {availableActions.find((a) => a.action === 'RELEASE_HOLD')?.isAvailable && (
            <Button size="sm" variant="outline" onClick={handleHoldToggle} disabled={isActionLoading}>
              Release Hold
            </Button>
          )}

          {payment.sapSyncStatus !== 'SYNCED' && (
            <Button
              size="sm"
              variant="outline"
              className="text-blue-600 border-blue-500/30 hover:bg-blue-500/10"
              onClick={() => setIsSapModalOpen(true)}
            >
              <Database className="h-4 w-4 mr-1.5" /> SAP F110 Post
            </Button>
          )}
        </div>
      </div>

      {/* Summary KPI Banner */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 rounded-2xl border border-border bg-card shadow-2xs font-poppins">
        <div>
          <span className="text-[10px] font-bold uppercase text-foreground-muted block">Disbursed Amount</span>
          <span className="text-xl font-black text-foreground font-mono">
            {formatInr(payment.amount)}
          </span>
          <span className="text-[10px] font-bold text-foreground-muted block mt-0.5">
            Type: {payment.paymentType}
          </span>
        </div>

        <div>
          <span className="text-[10px] font-bold uppercase text-foreground-muted block">Remaining Balance</span>
          <span className="text-xl font-black text-amber-600 font-mono">
            {formatInr(payment.remainingAmount)}
          </span>
          <span className="text-[10px] text-foreground-muted block mt-0.5">
            of {formatInr(payment.invoicePayableAmount)}
          </span>
        </div>

        <div>
          <span className="text-[10px] font-bold uppercase text-foreground-muted block">Bank Clearing Ref</span>
          <span className="text-sm font-mono font-black text-primary block truncate">
            {payment.bankReferenceNumber || 'Awaiting Clearing'}
          </span>
          <span className="text-[10px] text-foreground-muted block mt-0.5">{payment.paymentMethod}</span>
        </div>

        <div>
          <span className="text-[10px] font-bold uppercase text-foreground-muted block">SAP F110 Doc</span>
          <span className="text-sm font-mono font-black text-emerald-600 block truncate">
            {payment.sapDocumentNumber ? `Doc #${payment.sapDocumentNumber}` : 'Not Cleared'}
          </span>
          <span className="text-[10px] text-foreground-muted block mt-0.5">Fiscal Year 2026</span>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex border-b border-border overflow-x-auto gap-1 text-xs font-poppins">
        {[
          { key: 'overview', label: 'Overview & Summary', icon: IndianRupee },
          { key: 'traceability', label: 'Procure-to-Pay Traceability', icon: Layers },
          { key: 'readiness', label: 'Readiness & Eligibility', icon: ShieldCheck },
          { key: 'allocations', label: 'Allocations & History', icon: CreditCard },
          { key: 'failure', label: 'Failure & Diagnostics', icon: AlertTriangle },
          { key: 'reconciliation', label: 'Bank Reconciliation', icon: FileCheck },
          { key: 'sap', label: 'SAP F110 Clearing', icon: Database },
          { key: 'documents', label: 'Documents & Remittance', icon: FileText },
          { key: 'comments', label: 'Comments', icon: MessageSquare },
          { key: 'audit', label: 'Audit History', icon: History },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`flex items-center gap-2 px-3.5 py-2.5 font-bold border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                isActive
                  ? 'border-primary text-primary'
                  : 'border-transparent text-foreground-muted hover:text-foreground'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab 1: Overview */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Left Column: Commercial & Settlement Breakdown */}
          <div className="p-5 rounded-2xl border border-border bg-card space-y-4 shadow-2xs">
            <h3 className="text-sm font-bold text-foreground">Disbursement & Settlement Details</h3>
            <div className="space-y-2.5 text-xs">
              <div className="flex justify-between py-1 border-b border-border">
                <span className="text-foreground-muted">Payment Reference:</span>
                <span className="font-mono font-bold text-foreground">{payment.paymentReference}</span>
              </div>
              <div className="flex justify-between py-1 border-b border-border">
                <span className="text-foreground-muted">Invoice Total Payable:</span>
                <span className="font-bold text-foreground font-mono">
                  {formatInr(payment.invoicePayableAmount)}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-border">
                <span className="text-foreground-muted">Disbursed Amount (This Payment):</span>
                <span className="font-black text-emerald-600 font-mono">
                  {formatInr(payment.amount)}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-border">
                <span className="text-foreground-muted">Cumulative Paid on Invoice:</span>
                <span className="font-bold text-foreground font-mono">
                  {formatInr(payment.paidAmount)}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-border">
                <span className="text-foreground-muted">Outstanding Remaining Balance:</span>
                <span className="font-black text-amber-600 font-mono">
                  {formatInr(payment.remainingAmount)}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-border">
                <span className="text-foreground-muted">Settlement Classification:</span>
                <span className="font-bold text-foreground">{payment.paymentType} Payment</span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-foreground-muted">Clearing Channel:</span>
                <span className="font-bold text-foreground">{payment.paymentMethod}</span>
              </div>
            </div>
          </div>

          {/* Right Column: Beneficiary Bank & Target Dates */}
          <div className="space-y-5">
            {/* Beneficiary Coordinates */}
            <div className="p-5 rounded-2xl border border-border bg-card space-y-3.5 shadow-2xs">
              <div className="flex items-center gap-2">
                <Landmark className="h-4 w-4 text-primary" />
                <h3 className="text-sm font-bold text-foreground">Beneficiary Banking Master Data</h3>
              </div>
              <div className="p-3.5 bg-muted/40 rounded-xl border border-border space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-foreground-muted">Beneficiary Name:</span>
                  <span className="font-bold text-foreground">{payment.beneficiaryBank.accountHolderName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-foreground-muted">Bank Institution:</span>
                  <span className="font-bold text-foreground">{payment.beneficiaryBank.bankName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-foreground-muted">Account Number:</span>
                  <span className="font-mono font-bold text-foreground">
                    {payment.beneficiaryBank.maskedAccountNumber}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-foreground-muted">IFSC / Routing Code:</span>
                  <span className="font-mono font-bold text-foreground">{payment.beneficiaryBank.ifscCode}</span>
                </div>
              </div>
            </div>

            {/* Target Dates */}
            <div className="p-5 rounded-2xl border border-border bg-card space-y-3 shadow-2xs text-xs">
              <h3 className="text-sm font-bold text-foreground">Settlement Timeline & Milestones</h3>
              <div className="grid grid-cols-3 gap-2 pt-1">
                <div className="p-2.5 bg-muted/30 rounded-xl border border-border text-center">
                  <span className="text-[10px] text-foreground-muted block">Scheduled Date</span>
                  <span className="font-bold text-foreground">{formatDate(payment.scheduledDate)}</span>
                </div>
                <div className="p-2.5 bg-muted/30 rounded-xl border border-border text-center">
                  <span className="text-[10px] text-foreground-muted block">Disbursement Date</span>
                  <span className="font-bold text-emerald-600">{formatDate(payment.paymentDate)}</span>
                </div>
                <div className="p-2.5 bg-muted/30 rounded-xl border border-border text-center">
                  <span className="text-[10px] text-foreground-muted block">Payment Due Date</span>
                  <span className={payment.isOverdue ? 'font-bold text-rose-600' : 'font-bold text-foreground'}>
                    {formatDate(payment.dueDate)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab 2: Procure-to-Pay Traceability */}
      {activeTab === 'traceability' && (
        <div className="p-6 rounded-2xl border border-border bg-card space-y-6 shadow-2xs">
          <div>
            <h3 className="text-sm font-bold text-foreground">Full Procure-to-Pay Traceability Stream</h3>
            <p className="text-xs text-foreground-muted">
              End-to-end audit trace from purchase order commitment to warehouse receipt, invoice, and electronic payment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Step 1: Purchase Order */}
            <div
              onClick={() => navigate(ROUTES.PURCHASE_ORDERS.DETAILS(payment.poId || payment.poNumber))}
              className="p-4 rounded-xl border border-border bg-muted/20 hover:border-primary/50 transition-all cursor-pointer space-y-2 group"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase text-foreground-muted">1. PO Commitment</span>
                <ExternalLink className="h-3.5 w-3.5 text-foreground-muted group-hover:text-primary" />
              </div>
              <p className="font-mono font-bold text-sm text-primary">{payment.poNumber}</p>
              <p className="text-xs text-foreground-secondary truncate">{payment.vendorName}</p>
            </div>

            {/* Step 2: Warehouse GRN */}
            <div
              onClick={() => {
                if (payment.grnId || payment.grnNumber) {
                  navigate(ROUTES.GRN.DETAILS(payment.grnId || payment.grnNumber || ''));
                }
              }}
              className="p-4 rounded-xl border border-border bg-muted/20 hover:border-primary/50 transition-all cursor-pointer space-y-2 group"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase text-foreground-muted">2. Goods Receipt (GRN)</span>
                <ExternalLink className="h-3.5 w-3.5 text-foreground-muted group-hover:text-primary" />
              </div>
              <p className="font-mono font-bold text-sm text-emerald-600">
                {payment.grnNumber || 'Physical Receipt Inwarded'}
              </p>
              <p className="text-xs text-foreground-secondary">Warehouse Stock Inwarded</p>
            </div>

            {/* Step 3: Verified Invoice */}
            <div
              onClick={() => navigate(ROUTES.INVOICES.DETAILS(payment.invoiceId || payment.invoiceNumber))}
              className="p-4 rounded-xl border border-border bg-muted/20 hover:border-primary/50 transition-all cursor-pointer space-y-2 group"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold uppercase text-foreground-muted">3. Approved Invoice</span>
                <ExternalLink className="h-3.5 w-3.5 text-foreground-muted group-hover:text-primary" />
              </div>
              <p className="font-mono font-bold text-sm text-purple-600">{payment.invoiceNumber}</p>
              <p className="text-xs text-foreground-secondary">
                Total: {formatInr(payment.invoicePayableAmount)}
              </p>
            </div>

            {/* Step 4: Electronic Payment */}
            <div className="p-4 rounded-xl border-2 border-primary/40 bg-primary/5 space-y-2">
              <span className="text-[10px] font-bold uppercase text-primary">4. Treasury Disbursement</span>
              <p className="font-mono font-black text-sm text-foreground">{payment.paymentReference}</p>
              <p className="text-xs font-bold text-emerald-600">
                Disbursed: {formatInr(payment.amount)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tab 3: Readiness & Eligibility */}
      {activeTab === 'readiness' && (
        <div className="p-6 rounded-2xl border border-border bg-card space-y-5 shadow-2xs">
          <div>
            <h3 className="text-sm font-bold text-foreground">Payment Readiness & Pre-Disbursement Checklist</h3>
            <p className="text-xs text-foreground-muted">
              Evaluates all 8 procurement criteria before treasury disbursement release.
            </p>
          </div>

          <div className="space-y-3">
            {payment.readinessChecklist.map((chk) => (
              <div
                key={chk.id}
                className="p-3.5 rounded-xl border border-border bg-muted/30 flex items-center justify-between text-xs"
              >
                <div className="flex items-center gap-3">
                  {chk.passed ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0" />
                  )}
                  <div>
                    <span className="font-bold text-foreground block">{chk.label}</span>
                    <span className="text-foreground-muted text-[11px]">{chk.details}</span>
                  </div>
                </div>
                <span
                  className={`px-2.5 py-0.5 rounded-full font-bold text-[10px] ${
                    chk.passed
                      ? 'bg-emerald-500/10 text-emerald-600'
                      : 'bg-rose-500/10 text-rose-600'
                  }`}
                >
                  {chk.passed ? 'PASSED' : 'BLOCKED'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab 4: Allocations & History */}
      {activeTab === 'allocations' && (
        <div className="p-6 rounded-2xl border border-border bg-card space-y-4 shadow-2xs">
          <h3 className="text-sm font-bold text-foreground">Disbursement Allocation Breakdown</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead>
                <tr className="border-b border-border text-foreground-muted font-semibold">
                  <th className="py-2.5 px-3">Allocation ID</th>
                  <th className="py-2.5 px-3">Target Invoice</th>
                  <th className="py-2.5 px-3">Allocated Amount</th>
                  <th className="py-2.5 px-3">Currency</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {payment.allocations.map((alloc) => (
                  <tr key={alloc.id} className="hover:bg-muted/20">
                    <td className="py-2.5 px-3 font-mono font-bold text-primary">{alloc.id}</td>
                    <td className="py-2.5 px-3 font-mono text-purple-600 font-bold">{alloc.invoiceNumber}</td>
                    <td className="py-2.5 px-3 font-bold text-foreground font-mono">
                      {formatInr(alloc.allocatedAmount)}
                    </td>
                    <td className="py-2.5 px-3 font-mono text-foreground-muted">INR</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab 5: Failure & Diagnostics */}
      {activeTab === 'failure' && (
        <div className="p-6 rounded-2xl border border-border bg-card space-y-4 shadow-2xs">
          <h3 className="text-sm font-bold text-foreground">Failure Telemetry & Diagnostics</h3>
          {payment.status === 'FAILED' ? (
            <div className="space-y-3">
              <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-xl space-y-1 text-xs">
                <div className="flex items-center gap-2 text-rose-700 dark:text-rose-400 font-bold">
                  <AlertTriangle className="h-4 w-4" />
                  <span>Failure Code: {payment.failureCode || 'UNKNOWN_ERROR'}</span>
                </div>
                <p className="text-rose-600/90 text-xs">{payment.failureReason}</p>
                <p className="text-[11px] text-foreground-muted pt-1">
                  Attempt count: {payment.retryCount} / {payment.maxRetries}
                </p>
              </div>
              <Button size="sm" onClick={handleRetry} disabled={payment.retryCount >= payment.maxRetries}>
                <RefreshCw className="h-4 w-4 mr-1.5" /> Retry Disbursement
              </Button>
            </div>
          ) : (
            <div className="p-8 text-center text-xs text-foreground-muted">
              <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
              <span>No failure telemetry recorded for this payment.</span>
            </div>
          )}
        </div>
      )}

      {/* Tab 6: Bank Reconciliation */}
      {activeTab === 'reconciliation' && (
        <div className="p-6 rounded-2xl border border-border bg-card space-y-5 shadow-2xs">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-foreground">Bank Statement Reconciliation Matrix</h3>
              <p className="text-xs text-foreground-muted">
                Compares ProcureAI internal disbursement records against external bank clearing feeds.
              </p>
            </div>
            <span
              className={`px-3 py-1 rounded-full font-bold text-xs ${
                payment.reconciliationStatus === 'MATCHED'
                  ? 'bg-emerald-500/10 text-emerald-600'
                  : 'bg-amber-500/10 text-amber-600'
              }`}
            >
              {payment.reconciliationStatus}
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
            <div className="p-4 bg-muted/40 rounded-xl border border-border space-y-2">
              <span className="font-bold text-foreground block">Internal ProcureAI Instruction</span>
              <div className="flex justify-between">
                <span className="text-foreground-muted">Disbursed Amount:</span>
                <span className="font-bold font-mono">{formatInr(payment.amount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground-muted">Payment Reference:</span>
                <span className="font-mono font-bold">{payment.paymentReference}</span>
              </div>
            </div>

            <div className="p-4 bg-muted/40 rounded-xl border border-border space-y-2">
              <span className="font-bold text-foreground block">External Bank Statement</span>
              <div className="flex justify-between">
                <span className="text-foreground-muted">Bank Cleared Amount:</span>
                <span className="font-bold font-mono">
                  {formatInr(payment.reconciliationDetails?.externalAmount || payment.amount)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-foreground-muted">External Bank UTR:</span>
                <span className="font-mono font-bold">{payment.bankReferenceNumber || 'None'}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab 7: SAP F110 Run */}
      {activeTab === 'sap' && (
        <div className="p-6 rounded-2xl border border-border bg-card space-y-4 shadow-2xs">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-bold text-foreground">SAP S/4HANA F110 Payment Run Clearing</h3>
              <p className="text-xs text-foreground-muted">
                Bi-directional ERP clearing status and BAPI_ACC_DOCUMENT_POST payload.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setIsSapModalOpen(true)}>
              <Database className="h-4 w-4 mr-1.5" /> Open SAP Inspector
            </Button>
          </div>

          <div className="p-4 bg-muted/30 rounded-xl border border-border space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-foreground-muted">SAP Clearing Status:</span>
              <span className="font-bold text-emerald-600">{payment.sapSyncStatus}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-foreground-muted">SAP Accounting Document:</span>
              <span className="font-mono font-bold text-foreground">
                {payment.sapDocumentNumber ? `Doc #${payment.sapDocumentNumber}` : 'Pending Post'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Tab 8: Documents */}
      {activeTab === 'documents' && (
        <div className="p-6 rounded-2xl border border-border bg-card shadow-2xs">
          <DocumentManagementPanel
            documents={documents}
            onUploadFile={async (file, category) => {
              if (!payment) return;
              const newDoc = await mockDocumentCommentService.uploadDocument('PAYMENT', payment.id, file, category);
              setDocuments((prev) => [newDoc, ...prev]);
            }}
            onDownloadFile={async (docId) => {
              await mockDocumentCommentService.downloadDocument(docId);
            }}
            onDeleteFile={async (docId) => {
              await mockDocumentCommentService.deleteDocument(docId);
              setDocuments((prev) => prev.filter((d) => d.id !== docId));
            }}
          />
        </div>
      )}

      {/* Tab 9: Comments */}
      {activeTab === 'comments' && (
        <div className="p-6 rounded-2xl border border-border bg-card shadow-2xs">
          <EnterpriseCommentThread
            comments={comments}
            onAddComment={async (text) => {
              if (!payment) return;
              const newComment = await mockDocumentCommentService.createComment('PAYMENT', payment.id, text);
              setComments((prev) => [...prev, newComment]);
            }}
            onUpdateComment={async (cId, text) => {
              await mockDocumentCommentService.updateComment(cId, text);
              setComments((prev) => prev.map((c) => (c.id === cId ? { ...c, commentText: text } : c)));
            }}
            onDeleteComment={async (cId) => {
              await mockDocumentCommentService.deleteComment(cId);
              setComments((prev) => prev.filter((c) => c.id !== cId));
            }}
          />
        </div>
      )}

      {/* Tab 10: Audit */}
      {activeTab === 'audit' && (
        <div className="p-6 rounded-2xl border border-border bg-card shadow-2xs">
          <PaymentAuditHistoryTab auditHistory={payment.auditHistory} />
        </div>
      )}

      {/* Modals */}
      {isSapModalOpen && (
        <PaymentSapSyncModal
          isOpen={isSapModalOpen}
          payment={payment}
          onClose={() => setIsSapModalOpen(false)}
          onSync={async () => {
            const res = await mockPaymentService.syncWithSapF110(payment.id);
            await loadPayment();
            return res;
          }}
        />
      )}

      {isReconcileModalOpen && (
        <PaymentReconcileModal
          isOpen={isReconcileModalOpen}
          payment={payment}
          onClose={() => setIsReconcileModalOpen(false)}
          onReconcile={async (notes) => {
            await mockPaymentService.reconcilePayment(payment.id, notes);
            await loadPayment();
          }}
        />
      )}
    </div>
  );
};
